import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { TextHeadingWithBody } from "@ui-kit/TextHeadingWithBody/TextHeadingWithBody";
import { TabSingleSelect } from "@ui-kit/TabButtonGroup/TabSingleSelect";
import { useTabSingleSelect } from "@ui-kit/TabButtonGroup/useTabSelect";
import dayjs from "dayjs";
import { Category } from "@modules/entities/Category/Category";
import { Image } from "@ui-kit/Image/Image";
import {
  Collapse,
  CollapseNumberedHeader,
  CollapseNumberedHeaderStyles,
} from "@ui-kit/Collapse/Collapse";
import { InlineMap } from "../../../components/Map/InlineMap";
import { TripPlan } from "@dto/TripBuilder";
import { ScrollHorizontal } from "@ui-kit/ScrollHorizontal/ScrollHorizontal";

interface TripBuilderResultsProps {
  tripPlan?: TripPlan;
}

const TripBuilderResults: FunctionComponent<TripBuilderResultsProps> = ({
  tripPlan,
}) => {
  const { t } = useTranslation();

  const { activeTab: activeDayIndex, onTabSelect } = useTabSingleSelect<number>(
    {
      initialTab: 0,
    },
  );

  if (!tripPlan) {
    throw new Error(
      "Trip builder results attempted to render but no trip plan was found",
    );
  }

  const dayTabs = tripPlan.days.map((day, index) => ({
    value: index,
    label: day.date ? dayjs(day.date).format("M/D") : `Day ${day.dayIndex}`,
  }));

  const collapseItems = tripPlan.days[activeDayIndex].entities.map(
    (entity, index) => ({
      key: `${activeDayIndex}-${entity.id}`,
      label: <CollapseNumberedHeader label={entity.name} index={index} />,
      children: (
        <div
          className="trip-builder-results-day-item"
          key={entity.id}
          data-testid={`trip-builder-results-day-item`}>
          <div className="cover-photo">
            <Image src={entity.coverPhotoUrl} />
          </div>
          <div className="entity-name">{entity.name}</div>
          <div className="date-time-container">
            <Category entity={entity} />
          </div>
          {entity.location?.streetAddress && (
            <div className="entity-address">
              {entity.location.streetAddress}
            </div>
          )}
        </div>
      ),
      styles: CollapseNumberedHeaderStyles(index),
    }),
  );

  return (
    <div className="trip-builder-results-container">
      <div className="trip-builder-results-days">
        <TextHeadingWithBody
          headingText={tripPlan.title}
          bodyText={t("screens.tripBuilder.results.subheading")}
          headingSize="Large"
          bodySize="Medium"
          align="start"
          reverse={true}
          gap={8}
        />
        <ScrollHorizontal>
          <TabSingleSelect
            tabs={dayTabs}
            activeTab={activeDayIndex}
            onTabSelect={onTabSelect}
            testID="trip-builder-results-day-selector"
            variant="outline"
          />
        </ScrollHorizontal>
        <div className="trip-builder-results-day">
          <TextHeadingWithBody
            // TODO: Uncomment when API returns title for each day
            // headingText={tripPlan.days[activeDayIndex].title || ""}
            headingText={""}
            bodyText={tripPlan.days[activeDayIndex].description || ""}
            headingSize="Small"
            bodySize="Large"
            align="start"
            gap={8}
          />
          <div
            className="trip-builder-results-day-items"
            data-testid="trip-builder-results-day-items">
            <Collapse
              items={collapseItems}
              ghost={true}
              bordered={false}
              expandIconPosition="end"
            />
          </div>
        </div>
      </div>
      <InlineMap
        locations={tripPlan.days[activeDayIndex].entities.map(
          (entity) => entity.location,
        )}
      />
    </div>
  );
};

export { TripBuilderResults };
